import { timeFunctionMulti } from './perf/log'
import { Position, posKeyXYZ } from './voxelMath'
import { Quaternion, Vector3 } from '@/math'
import { VoxelData, type Face } from './voxelTypes'

/**
 * Shape rasterization module
 * 
 * Each shape is defined by a simple test function that determines
 * if a voxel at position (x,y,z) is inside the shape.
 * 
 * All shapes are defined in local space (centered at origin)
 * and then transformed to world space by the caller.
 */

export type ShapeType = 'sphere' | 'cylinder' | 'cone'

export interface ShapeParams {
  center: Position
  radius: number
  voxel: VoxelData
  // Optional parameters for specific shapes
  height?: number  // For cylinder and cone
  axis?: 'x' | 'y' | 'z'  // Orientation axis, default 'y'
  axisSign?: 1 | -1
  rotation?: { x: number; y: number; z: number; w: number }
}

// Shapes stream results via a writer function: (key, voxel) => void

/**
 * Rasterize a sphere into voxels
 */
export function rasterizeSphere(
  center: Position,
  radius: number,
  voxel: VoxelData,
  bounds: Position | undefined,
  write: (key: number, voxel: VoxelData) => void
): void {
  // Backwards-compat: perfect sphere uses uniform radius
  timeFunctionMulti('rasterizeEllipsoid', ()=> rasterizeEllipsoid(center, radius, radius, radius, voxel, bounds, write))
}

// Rasterize an ellipsoid with independent radii along each axis
export function rasterizeEllipsoid(
  center: Position,
  rx: number,
  ry: number,
  rz: number,
  voxel: VoxelData,
  bounds: Position | undefined,
  write: (key: number, voxel: VoxelData) => void
): void {

  let minX = Math.floor(center.x - rx)
  let maxX = Math.ceil(center.x + rx)
  let minY = Math.floor(center.y - ry)
  let maxY = Math.ceil(center.y + ry)
  let minZ = Math.floor(center.z - rz)
  let maxZ = Math.ceil(center.z + rz)
  
  // Apply bounds if provided
  if (bounds) {
    minX = Math.max(minX, 0)
    maxX = Math.min(maxX, bounds.x - 1)
    minY = Math.max(minY, 0)
    maxY = Math.min(maxY, bounds.y - 1)
    minZ = Math.max(minZ, 0)
    maxZ = Math.min(maxZ, bounds.z - 1)
  }

  const invRx2 = 1 / (rx * rx)
  const invRy2 = 1 / (ry * ry)
  const invRz2 = 1 / (rz * rz)

  for (let x = minX; x <= maxX; x++) {
    for (let y = minY; y <= maxY; y++) {
      for (let z = minZ; z <= maxZ; z++) {
        const dx = x - center.x
        const dy = y - center.y
        const dz = z - center.z
        const val = dx * dx * invRx2 + dy * dy * invRy2 + dz * dz * invRz2
        if (val <= 1) write(posKeyXYZ(x, y, z), voxel)
      }
    }
  }
}

/**
 * Rasterize a cube (axis-aligned box) with half-extent floor(radius)
 */
export function rasterizeCube(
  center: Position,
  radius: number,
  voxel: VoxelData,
  bounds: Position | undefined,
  write: (key: number, voxel: VoxelData) => void
): void {
  const h = Math.floor(radius)
  let minX = center.x - h
  let maxX = center.x + h
  let minY = center.y - h
  let maxY = center.y + h
  let minZ = center.z - h
  let maxZ = center.z + h

  if (bounds) {
    minX = Math.max(minX, 0)
    maxX = Math.min(maxX, bounds.x - 1)
    minY = Math.max(minY, 0)
    maxY = Math.min(maxY, bounds.y - 1)
    minZ = Math.max(minZ, 0)
    maxZ = Math.min(maxZ, bounds.z - 1)
  }

  for (let x = minX; x <= maxX; x++) {
    for (let y = minY; y <= maxY; y++) {
      for (let z = minZ; z <= maxZ; z++) {
        write(posKeyXYZ(x, y, z), voxel)
      }
    }
  }
}

/**
 * Rasterize a flat disc (1-voxel thick) perpendicular to a face direction.
 * The disc lies in the plane of the given face, centered at `center`.
 */
export function rasterizeDisc(
  center: Position,
  radius: number,
  face: Face,
  voxel: VoxelData,
  bounds: Position | undefined,
  write: (key: number, voxel: VoxelData) => void
): void {
  // Determine the two in-plane axes based on the face
  // y+/- => disc in XZ plane; x+/- => disc in YZ plane; z+/- => disc in XY plane
  const R = Math.floor(radius)
  const r2 = radius * radius

  // Compute loops in the appropriate 2D plane
  if (face[0] === 'y') {
    // XZ plane at fixed Y
    let minX = Math.floor(center.x - R)
    let maxX = Math.ceil(center.x + R)
    let minZ = Math.floor(center.z - R)
    let maxZ = Math.ceil(center.z + R)
    const y = center.y
    if (bounds) {
      minX = Math.max(minX, 0)
      maxX = Math.min(maxX, bounds.x - 1)
      minZ = Math.max(minZ, 0)
      maxZ = Math.min(maxZ, bounds.z - 1)
      if (y < 0 || y >= bounds.y) return
    }
    for (let x = minX; x <= maxX; x++) {
      for (let z = minZ; z <= maxZ; z++) {
        const dx = x - center.x
        const dz = z - center.z
        if (dx * dx + dz * dz <= r2) write(posKeyXYZ(x, y, z), voxel)
      }
    }
  } else if (face[0] === 'x') {
    // YZ plane at fixed X
    let minY = Math.floor(center.y - R)
    let maxY = Math.ceil(center.y + R)
    let minZ = Math.floor(center.z - R)
    let maxZ = Math.ceil(center.z + R)
    const x = center.x
    if (bounds) {
      minY = Math.max(minY, 0)
      maxY = Math.min(maxY, bounds.y - 1)
      minZ = Math.max(minZ, 0)
      maxZ = Math.min(maxZ, bounds.z - 1)
      if (x < 0 || x >= bounds.x) return
    }
    for (let y = minY; y <= maxY; y++) {
      for (let z = minZ; z <= maxZ; z++) {
        const dy = y - center.y
        const dz = z - center.z
        if (dy * dy + dz * dz <= r2) write(posKeyXYZ(x, y, z), voxel)
      }
    }
  } else {
    // XY plane at fixed Z
    let minX = Math.floor(center.x - R)
    let maxX = Math.ceil(center.x + R)
    let minY = Math.floor(center.y - R)
    let maxY = Math.ceil(center.y + R)
    const z = center.z
    if (bounds) {
      minX = Math.max(minX, 0)
      maxX = Math.min(maxX, bounds.x - 1)
      minY = Math.max(minY, 0)
      maxY = Math.min(maxY, bounds.y - 1)
      if (z < 0 || z >= bounds.z) return
    }
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        const dx = x - center.x
        const dy = y - center.y
        if (dx * dx + dy * dy <= r2) write(posKeyXYZ(x, y, z), voxel)
      }
    }
  }
}

/**
 * Rasterize a cylinder into voxels
 * Default orientation is along Y axis
 */
export function rasterizeCylinder(
  center: Position,
  radius: number,
  height: number,
  voxel: VoxelData,
  write: (key: number, voxel: VoxelData) => void,
  bounds?: Position,
  axis: 'x' | 'y' | 'z' = 'y',
): void {
  const r2 = radius * radius
  const halfHeight = height / 2
  
  // Calculate bounds based on axis
  let minX: number, maxX: number
  let minY: number, maxY: number
  let minZ: number, maxZ: number
  
  if (axis === 'y') {
    // Cylinder along Y axis
    minX = Math.floor(center.x - radius)
    maxX = Math.ceil(center.x + radius)
    minY = Math.floor(center.y - halfHeight)
    maxY = Math.ceil(center.y + halfHeight)
    minZ = Math.floor(center.z - radius)
    maxZ = Math.ceil(center.z + radius)
    
    // Apply bounds if provided
    if (bounds) {
      minX = Math.max(minX, 0)
      maxX = Math.min(maxX, bounds.x - 1)
      minY = Math.max(minY, 0)
      maxY = Math.min(maxY, bounds.y - 1)
      minZ = Math.max(minZ, 0)
      maxZ = Math.min(maxZ, bounds.z - 1)
    }
    
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        for (let z = minZ; z <= maxZ; z++) {
          const dx = x - center.x
          const dz = z - center.z
          const dy = y - center.y
          const dist2 = dx * dx + dz * dz  // Distance in XZ plane
          
          if (dist2 <= r2 && Math.abs(dy) <= halfHeight) write(posKeyXYZ(x, y, z), voxel)
        }
      }
    }
  } else if (axis === 'x') {
    // Cylinder along X axis
    minX = Math.floor(center.x - halfHeight)
    maxX = Math.ceil(center.x + halfHeight)
    minY = Math.floor(center.y - radius)
    maxY = Math.ceil(center.y + radius)
    minZ = Math.floor(center.z - radius)
    maxZ = Math.ceil(center.z + radius)
    
    // Apply bounds if provided
    if (bounds) {
      minX = Math.max(minX, 0)
      maxX = Math.min(maxX, bounds.x - 1)
      minY = Math.max(minY, 0)
      maxY = Math.min(maxY, bounds.y - 1)
      minZ = Math.max(minZ, 0)
      maxZ = Math.min(maxZ, bounds.z - 1)
    }
    
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        for (let z = minZ; z <= maxZ; z++) {
          const dy = y - center.y
          const dz = z - center.z
          const dx = x - center.x
          const dist2 = dy * dy + dz * dz  // Distance in YZ plane
          
          if (dist2 <= r2 && Math.abs(dx) <= halfHeight) write(posKeyXYZ(x, y, z), voxel)
        }
      }
    }
  } else {
    // Cylinder along Z axis
    minX = Math.floor(center.x - radius)
    maxX = Math.ceil(center.x + radius)
    minY = Math.floor(center.y - radius)
    maxY = Math.ceil(center.y + radius)
    minZ = Math.floor(center.z - halfHeight)
    maxZ = Math.ceil(center.z + halfHeight)
    
    // Apply bounds if provided
    if (bounds) {
      minX = Math.max(minX, 0)
      maxX = Math.min(maxX, bounds.x - 1)
      minY = Math.max(minY, 0)
      maxY = Math.min(maxY, bounds.y - 1)
      minZ = Math.max(minZ, 0)
      maxZ = Math.min(maxZ, bounds.z - 1)
    }
    
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        for (let z = minZ; z <= maxZ; z++) {
          const dx = x - center.x
          const dy = y - center.y
          const dz = z - center.z
          const dist2 = dx * dx + dy * dy  // Distance in XY plane
          
          if (dist2 <= r2 && Math.abs(dz) <= halfHeight) write(posKeyXYZ(x, y, z), voxel)
        }
      }
    }
  }
}

/**
 * Rasterize a cone into voxels
 * Default orientation is along Y axis with tip pointing up
 */
export function rasterizeCone(
  center: Position,
  radius: number,
  height: number,
  voxel: VoxelData,
  write: (key: number, voxel: VoxelData) => void,
  bounds: Position | undefined,
  axis: 'x' | 'y' | 'z' = 'y',
  axisSign: 1 | -1 = 1,
): void {
  const dir = axisSign >= 0 ? 1 : -1
  
  // Calculate bounds based on axis
  let minX: number, maxX: number
  let minY: number, maxY: number
  let minZ: number, maxZ: number
  
  if (axis === 'y') {
    // Cone along Y axis (tip pointing dir)
    minX = Math.floor(center.x - radius)
    maxX = Math.ceil(center.x + radius)
    if (dir === 1) {
      minY = Math.floor(center.y)
      maxY = Math.ceil(center.y + height)
    } else {
      minY = Math.floor(center.y - height)
      maxY = Math.ceil(center.y)
    }
    minZ = Math.floor(center.z - radius)
    maxZ = Math.ceil(center.z + radius)
    
    // Apply bounds if provided
    if (bounds) {
      minX = Math.max(minX, 0)
      maxX = Math.min(maxX, bounds.x - 1)
      minY = Math.max(minY, 0)
      maxY = Math.min(maxY, bounds.y - 1)
      minZ = Math.max(minZ, 0)
      maxZ = Math.min(maxZ, bounds.z - 1)
    }
    
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        for (let z = minZ; z <= maxZ; z++) {
          const dx = x - center.x
          const dy = (y - center.y) * dir
          const dz = z - center.z
          
          // Calculate radius at this height
          const heightRatio = 1 - (dy / height)
          if (heightRatio <= 0 || heightRatio > 1) continue
          
          const radiusAtHeight = radius * heightRatio
          const dist2 = dx * dx + dz * dz  // Distance in XZ plane
          
          if (dist2 <= radiusAtHeight * radiusAtHeight) write(posKeyXYZ(x, y, z), voxel)
        }
      }
    }
  } else if (axis === 'x') {
    // Cone along X axis (tip pointing dir)
    if (dir === 1) {
      minX = Math.floor(center.x)
      maxX = Math.ceil(center.x + height)
    } else {
      minX = Math.floor(center.x - height)
      maxX = Math.ceil(center.x)
    }
    minY = Math.floor(center.y - radius)
    maxY = Math.ceil(center.y + radius)
    minZ = Math.floor(center.z - radius)
    maxZ = Math.ceil(center.z + radius)
    
    // Apply bounds if provided
    if (bounds) {
      minX = Math.max(minX, 0)
      maxX = Math.min(maxX, bounds.x - 1)
      minY = Math.max(minY, 0)
      maxY = Math.min(maxY, bounds.y - 1)
      minZ = Math.max(minZ, 0)
      maxZ = Math.min(maxZ, bounds.z - 1)
    }
    
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        for (let z = minZ; z <= maxZ; z++) {
          const dx = (x - center.x) * dir
          const dy = y - center.y
          const dz = z - center.z
          
          // Calculate radius at this distance along X
          const heightRatio = 1 - (dx / height)
          if (heightRatio <= 0 || heightRatio > 1) continue
          
          const radiusAtHeight = radius * heightRatio
          const dist2 = dy * dy + dz * dz  // Distance in YZ plane
          
          if (dist2 <= radiusAtHeight * radiusAtHeight) {
            write(posKeyXYZ(x, y, z), voxel)
          }
        }
      }
    }
  } else {
    // Cone along Z axis (tip pointing dir)
    minX = Math.floor(center.x - radius)
    maxX = Math.ceil(center.x + radius)
    minY = Math.floor(center.y - radius)
    maxY = Math.ceil(center.y + radius)
    if (dir === 1) {
      minZ = Math.floor(center.z)
      maxZ = Math.ceil(center.z + height)
    } else {
      minZ = Math.floor(center.z - height)
      maxZ = Math.ceil(center.z)
    }
    
    // Apply bounds if provided
    if (bounds) {
      minX = Math.max(minX, 0)
      maxX = Math.min(maxX, bounds.x - 1)
      minY = Math.max(minY, 0)
      maxY = Math.min(maxY, bounds.y - 1)
      minZ = Math.max(minZ, 0)
      maxZ = Math.min(maxZ, bounds.z - 1)
    }
    
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        for (let z = minZ; z <= maxZ; z++) {
          const dx = x - center.x
          const dy = y - center.y
          const dz = (z - center.z) * dir
          
          // Calculate radius at this distance along Z
          const heightRatio = 1 - (dz / height)
          if (heightRatio <= 0 || heightRatio > 1) continue
          
          const radiusAtHeight = radius * heightRatio
          const dist2 = dx * dx + dy * dy  // Distance in XY plane
          
          if (dist2 <= radiusAtHeight * radiusAtHeight) write(posKeyXYZ(x, y, z), voxel)
        }
      }
    }
  }
}

/**
 * Get bounding box for a shape
 */
export function getShapeBounds(
  type: ShapeType,
  center: Position,
  radius: number,
  height?: number,
  axis: 'x' | 'y' | 'z' = 'y'
): { min: Position; max: Position } {
  switch (type) {
    case 'sphere': {
      const rAxis = height ?? radius
      if (axis === 'y') {
        return {
          min: { x: center.x - radius, y: center.y - rAxis, z: center.z - radius },
          max: { x: center.x + radius, y: center.y + rAxis, z: center.z + radius },
        }
      } else if (axis === 'x') {
        return {
          min: { x: center.x - rAxis, y: center.y - radius, z: center.z - radius },
          max: { x: center.x + rAxis, y: center.y + radius, z: center.z + radius },
        }
      } else {
        return {
          min: { x: center.x - radius, y: center.y - radius, z: center.z - rAxis },
          max: { x: center.x + radius, y: center.y + radius, z: center.z + rAxis },
        }
      }
    }
    
    case 'cylinder': {
      const h = height || radius * 2  // Default height = diameter
      const halfHeight = h / 2
      
      if (axis === 'y') {
        return {
          min: { x: center.x - radius, y: center.y - halfHeight, z: center.z - radius },
          max: { x: center.x + radius, y: center.y + halfHeight, z: center.z + radius }
        }
      } else if (axis === 'x') {
        return {
          min: { x: center.x - halfHeight, y: center.y - radius, z: center.z - radius },
          max: { x: center.x + halfHeight, y: center.y + radius, z: center.z + radius }
        }
      } else {
        return {
          min: { x: center.x - radius, y: center.y - radius, z: center.z - halfHeight },
          max: { x: center.x + radius, y: center.y + radius, z: center.z + halfHeight }
        }
      }
    }
    
    case 'cone': {
      const h = height || radius * 2  // Default height = diameter
      
      if (axis === 'y') {
        return {
          min: { x: center.x - radius, y: center.y, z: center.z - radius },
          max: { x: center.x + radius, y: center.y + h, z: center.z + radius }
        }
      } else if (axis === 'x') {
        return {
          min: { x: center.x, y: center.y - radius, z: center.z - radius },
          max: { x: center.x + h, y: center.y + radius, z: center.z + radius }
        }
      } else {
        return {
          min: { x: center.x - radius, y: center.y - radius, z: center.z },
          max: { x: center.x + radius, y: center.y + radius, z: center.z + h }
        }
      }
    }
  }
}

/**
 * Main shape rasterization function
 */
export function rasterizeShape(
  type: ShapeType,
  params: ShapeParams,
  bounds: Position | undefined,
  write: (key: number, voxel: VoxelData) => void
): void {
  const { center, radius, voxel, height, axis = 'y', axisSign = 1 } = params
  if (params.rotation) {
    return rasterizeShapeTransformed(type, params, bounds, write)
  }
  
  switch (type) {
    case 'sphere': {
      const rAxis = height ?? radius
      if (axis === 'y') rasterizeEllipsoid(center, radius, rAxis, radius, voxel, bounds, write)
      else if (axis === 'x') rasterizeEllipsoid(center, rAxis, radius, radius, voxel, bounds, write)
      else rasterizeEllipsoid(center, radius, radius, rAxis, voxel, bounds, write)
      break
    }
    
    case 'cylinder':
      rasterizeCylinder(center, radius, height || radius * 2, voxel, write, bounds, axis)
      break
    
    case 'cone':
      rasterizeCone(center, radius, height || radius * 2, voxel, write, bounds, axis, axisSign)
      break
    
    default:
      console.warn(`Unknown shape type: ${type}`)
      break
  }
}

// Generic transformed rasterizer using quaternion rotation around center
export function rasterizeShapeTransformed(
  type: ShapeType,
  params: ShapeParams,
  bounds: Position | undefined,
  write: (key: number, voxel: VoxelData) => void
): void {
  const { center, radius, voxel } = params
  const height = params.height || (type === 'sphere' ? radius : Math.max(1, radius * 2))
  const q = params.rotation ? new Quaternion(params.rotation.x, params.rotation.y, params.rotation.z, params.rotation.w) : new Quaternion()
  const invQ = q.clone().invert()

  // Build unrotated local AABB (axis along +Y)
  let localMin: Position
  let localMax: Position
  if (type === 'sphere') {
    const rAxis = height || radius
    // Ellipsoid radii along X,Y,Z
    const rx = radius, ry = rAxis, rz = radius
    localMin = { x: center.x - rx, y: center.y - ry, z: center.z - rz }
    localMax = { x: center.x + rx, y: center.y + ry, z: center.z + rz }
  } else if (type === 'cylinder') {
    const halfH = (height || radius * 2) / 2
    localMin = { x: center.x - radius, y: center.y - halfH, z: center.z - radius }
    localMax = { x: center.x + radius, y: center.y + halfH, z: center.z + radius }
  } else { // cone
    const h = height || radius * 2
    localMin = { x: center.x - radius, y: center.y, z: center.z - radius }
    localMax = { x: center.x + radius, y: center.y + h, z: center.z + radius }
  }

  // Rotate local AABB corners to world, compute axis-aligned AABB
  const corners: Array<Vector3> = []
  ;[
    [localMin.x, localMin.y, localMin.z],
    [localMin.x, localMin.y, localMax.z],
    [localMin.x, localMax.y, localMin.z],
    [localMin.x, localMax.y, localMax.z],
    [localMax.x, localMin.y, localMin.z],
    [localMax.x, localMin.y, localMax.z],
    [localMax.x, localMax.y, localMin.z],
    [localMax.x, localMax.y, localMax.z],
  ].forEach(([x,y,z]) => {
    const v = new Vector3(x - center.x, y - center.y, z - center.z)
    v.applyQuaternion(q)
    v.add(new Vector3(center.x, center.y, center.z))
    corners.push(v)
  })
  let minX = Infinity, minY = Infinity, minZ = Infinity
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity
  for (const v of corners) {
    if (v.x < minX) minX = v.x; if (v.y < minY) minY = v.y; if (v.z < minZ) minZ = v.z
    if (v.x > maxX) maxX = v.x; if (v.y > maxY) maxY = v.y; if (v.z > maxZ) maxZ = v.z
  }
  let minXi = Math.floor(minX), maxXi = Math.ceil(maxX)
  let minYi = Math.floor(minY), maxYi = Math.ceil(maxY)
  let minZi = Math.floor(minZ), maxZi = Math.ceil(maxZ)
  if (bounds) {
    minXi = Math.max(minXi, 0)
    maxXi = Math.min(maxXi, bounds.x - 1)
    minYi = Math.max(minYi, 0)
    maxYi = Math.min(maxYi, bounds.y - 1)
    minZi = Math.max(minZi, 0)
    maxZi = Math.min(maxZi, bounds.z - 1)
  }

  const axisSign = params.axisSign ?? 1

  // Radii used for tests in local coordinates (axis along +Y)
  const rAxis = (type === 'sphere') ? (params.height ?? radius) : radius
  const rx = radius
  const ry = (type === 'sphere') ? rAxis : undefined
  const rz = radius
  const halfH = (height || radius * 2) / 2
  const H = height || radius * 2

  const local = new Vector3()
  for (let x = minXi; x <= maxXi; x++) {
    for (let y = minYi; y <= maxYi; y++) {
      for (let z = minZi; z <= maxZi; z++) {
        // Transform world voxel center to local space
        local.set(x - center.x, y - center.y, z - center.z)
        local.applyQuaternion(invQ)
        // Flip along Y for negative axisSign
        const lx = local.x
        const ly = local.y * axisSign
        const lz = local.z
        let inside = false
        if (type === 'sphere') {
          const irx2 = 1 / (rx * rx)
          const iry2 = 1 / ((ry || rx) * (ry || rx))
          const irz2 = 1 / (rz * rz)
          const val = lx * lx * irx2 + ly * ly * iry2 + lz * lz * irz2
          inside = val <= 1
        } else if (type === 'cylinder') {
          const dist2 = lx * lx + lz * lz
          inside = (dist2 <= radius * radius) && (Math.abs(ly) <= halfH)
        } else { // cone
          const h = H
          const y0 = ly
          if (y0 >= 0 && y0 <= h) {
            const t = 1 - (y0 / h)
            const rr = radius * t
            const dist2 = lx * lx + lz * lz
            inside = dist2 <= rr * rr
          } else inside = false
        }
        if (inside) write(posKeyXYZ(x, y, z), voxel)
      }
    }
  }
}
